import * as React from "react";

import * as Redux from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { useUserState } from "client/hooks";
import useExperimentGroup from "client/hooks/useExperimentGroup";
import { useMerchantModel } from "client/hooks/useMerchantModel";
import { OnboardingStatus } from "client/lib/apt-merchant";
import * as UserService from "client/services/user";
import { FormCompletion, RootState, UserState } from "client/store";
import {
  getRouteFromValidationError,
  isOnBusinessDetailsRoute,
  routes,
  RouteWithoutBase,
} from "client/utils/route-utils";
import { removeSearchParamsFromUrlString } from "client/utils/url";

export const convertUserStateAndRestoreToRoute = ({
  formCompletedState,
  onboardingStatus,
  userState,
}: ConvertStateToRoute): RouteWithoutBase | null => {
  if (!formCompletedState || !userState) {
    return null;
  }

  let route: RouteWithoutBase | null;

  // will eventually be refactored to handle multiple opportunities
  if (onboardingStatus === "INELIGIBLE") {
    route = routes.status;
  } else if (userState !== "POST_LEAD") {
    route = userStateRouteMap[userState];
  } else {
    route = convertFormCompletionToRoute(formCompletedState);
  }
  return route;
};

type UserStateToRouteMap = Record<UserState, RouteWithoutBase | null>;

export const userStateRouteMap: UserStateToRouteMap = {
  AGENCY: "/agency",
  INITIAL: null,
  INVALID_COUNTRY: "/invalid-country",
  LOGGED_OUT: null,
  NEW_CONTACT_FOR_ACCOUNT: "/lead",
  POST_LEAD: "/onboarding",
  PRE_LEAD: "/lead",
  SQUARE_ONLY: "/square",
  SQUARE_AND_MOP: "/square",
  SQUARESPACE: "/squarespace",
  MAROPOST: "/maropost",
  THANK_YOU: "/thank-you",
  WAIT_FOR_EMAIL: "/check-email",
  WRONG_ACCOUNT: "/wrong-email",
  MAINTENANCE_MODE: "/maintenance",
};

const convertFormCompletionToRoute = (
  formCompletedState: FormCompletion
): RouteWithoutBase => {
  const {
    bankForm,
    businessOwnerForm,
    companyInfoForm,
    customerServiceForm,
    storeDetailsForm,
    storeForm,
    cashPayForm,
  } = formCompletedState as FormCompletion;

  if (
    bankForm &&
    businessOwnerForm &&
    companyInfoForm &&
    customerServiceForm &&
    storeDetailsForm &&
    storeForm &&
    cashPayForm
  ) {
    return "/status";
  }

  if (
    !companyInfoForm &&
    !storeDetailsForm &&
    !businessOwnerForm &&
    storeForm
  ) {
    /**
     * Until salesforce data patch occurs, if a legacy merchant
     * has completed the MOP, storeForm will be complete but all others
     * will not be. This handles that edge case. This entire if block will
     * be removed after salesforce data patch.
     */
    /**
     * If storeForm IS complete, still check to see if user needs to fill out
     * bankForm or customerServiceForm. Otherwise, route to status. This covers
     * the case where a merchant started on legacy, finished the storeForm, but
     * then was moved to redesign.
     */
    if (!bankForm) {
      return routes.onboardingBankInfo;
    } else if (!customerServiceForm) {
      return routes.onboardingCustomerSupport;
    } else {
      return routes.status;
    }
  }
  if (!companyInfoForm) {
    return routes.onboardingCompanyInfo;
  } else if (!storeDetailsForm) {
    return routes.onboardingStoreDetails;
  } else if (!businessOwnerForm) {
    return routes.onboardingOwners;
  } else if (!bankForm) {
    return routes.onboardingBankInfo;
  } else if (!customerServiceForm) {
    return routes.onboardingCustomerSupport;
  } else {
    return routes.status;
  }
};

type ConvertStateToRoute = Partial<UserService.UserStateAndRestoreResponse> & {
  onboardingStatus?: OnboardingStatus;
};

type UserRouteCtx = {
  setRoute: (route: RouteWithoutBase) => void;
  route: RouteWithoutBase | null;
};

const RouteContext = React.createContext<UserRouteCtx>({
  setRoute: () => {
    // noop
  },
  route: null,
});

export const useUserRoute = (): UserRouteCtx => {
  const ctx = React.useContext(RouteContext);
  if (ctx === undefined) {
    throw new Error(`useUserRoute must be used within a RouteContext provider`);
  }
  return ctx;
};

export const RouteProvider: React.FC = ({ children }) => {
  const { merchant } = useMerchantModel();
  const navigateTo = useNavigate();
  const location = useLocation();

  const [route, setRoute] = React.useState<RouteWithoutBase | null>(null);
  const {
    formCompletion,
    merchant: {
      merchantAccount: { onboardingStatus },
    },
    userState: { userState },
    error: { fieldErrorMap },
  } = Redux.useSelector(
    ({ formCompletion, merchant, userState, error }: RootState) => ({
      formCompletion,
      merchant,
      userState,
      error,
    })
  );

  useUserState();
  useExperimentGroup();

  React.useEffect(() => {
    const url = window.location.href;
    // Since this impacts routing at the top level, it takes priority over
    // form completion routing. In order to route to Status page on cash form submission we need to include this check
    if (url.includes("/cash") && !formCompletion.cashPayForm) {
      setRoute(routes.cashForm);
      return;
    }
    const _route = convertUserStateAndRestoreToRoute({
      formCompletedState: formCompletion,
      onboardingStatus,
      userState,
    });

    if (_route) {
      setRoute(_route);
    }
  }, [formCompletion, onboardingStatus, userState]);

  React.useEffect(() => {
    // open up specific forms to surface BE validation errors
    if (isOnBusinessDetailsRoute(location.pathname)) {
      const routeWithErrors = getRouteFromValidationError(fieldErrorMap);
      if (routeWithErrors) {
        setRoute(routeWithErrors);
      }
    }
  }, [fieldErrorMap, merchant, location.pathname]);

  React.useEffect(() => {
    // We track merchants who are arriving to MOP through onboarding or verify email link through query parameters with param key ="ol_src"
    // Once that event is fired after useQuery fetch, we don't want to fire off duplicate events so we want to remove that specific query param from the url without stripping other parameters such as utm params
    const urlString = removeSearchParamsFromUrlString(
      window.location.href,
      "ol_src"
    );
    const newSearchString = !urlString
      ? window?.location?.search
      : new URL(urlString).search;
    if (route && location.pathname !== route) {
      navigateTo({
        pathname: route,
        search: newSearchString,
      });
    }
  }, [location, navigateTo, route]);
  return (
    <RouteContext.Provider value={{ setRoute, route }}>
      {children}
    </RouteContext.Provider>
  );
};
